import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { Entity } from "@common/types/entity";
import { ComponentPropsWithRef, ReactElement, ReactNode } from "react";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";

export interface EntityFilterType {
    entityIds?: number[];
    /** Only emitted by pickers with `showNoEntityOption`; true when the "No entity" row is picked. */
    hasNoEntity?: boolean;
}

/** Picker value of the "No entity" row; entity rows carry their numeric id. */
export const NO_ENTITY_VALUE = "no-entity";
export const NO_ENTITY_LABEL = "No entity";

type EntityFilterMode = "filter" | "selection";
type EntityFilterSelectionMode = "single" | "multiple";

export interface EntityFilterProps extends Pick<
    ValueControlProps<EntityFilterType, HTMLButtonElement>,
    "onBlur" | "invalid" | "ref"
> {
    filters: EntityFilterType;
    onChange: (filters: EntityFilterType) => void;
    onlyBusiness?: boolean;
    container?: HTMLElement;
    showLabel?: boolean;
    excludeAllMockEntities?: boolean;
    excludeEmptyMockEntities?: ExcludeEmptyMockEntitiesMode;
    mode?: EntityFilterMode;
    selectionMode?: EntityFilterSelectionMode;
    mutuallyExclusive?: boolean;
    /** Multi mode only: appends a "No entity" row that maps to `hasNoEntity`. */
    showNoEntityOption?: boolean;
    disabled?: boolean;
    isEntityDisabled?: (entity: Entity) => boolean;
    disabledEntityTooltip?: ReactNode;
    triggerButtonProps?: TooltipTriggerButtonProps;
    /** Id of a visible element elsewhere that names the trigger, custom or not. */
    "aria-labelledby"?: string;
    /** The popup's accessible name (a grid column's name). */
    popupLabel?: string;
    /** Controls whether the popup is open. */
    open?: boolean;
    /** Called alongside the popup's own close handling. */
    onOpenChange?: (open: boolean) => void;
    renderTrigger?: (props: {
        triggerProps: ComponentPropsWithRef<"button">;
        selectedEntities: Entity[];
    }) => ReactElement;
}
